import React, { createContext, useReducer, useContext } from 'react';
import PropTypes from 'prop-types';

const ProductContext = createContext();

const productReducer = (state, action) => {
  switch (action.type) {
    case 'UPDATE_WARNING_MESSAGE': {
      return {
        ...state,
        warning: action.payload
      };
    }

    case 'UPDATE_QUANTITY': {
      return {
        ...state,
        quantityChange: action.payload
      };
    }

    case 'UPDATE_BUTTON_TEXT': {
      return {
        ...state,
        buttonText: action.payload
      };
    }

    case 'SET_STICKY_ADDED_TO_CART': {
      return {
        ...state,
        stickyAddedToCart: action.payload
      };
    }

    case 'SET_ONPAGE_ADDED_TO_CART': {
      return {
        ...state,
        onPageAddedToCart: action.payload
      };
    }

    case 'SET_REQUIRE_MONOGRAM': {
      return {
        ...state,
        requireMonogram: action.payload
      };
    }

    case 'SET_WISHLIST_REF': {
      return {
        ...state,
        wishlistRef: action.payload
      };
    }

    case 'SET_MONOGRAM_FORM_REF': {
      return {
        ...state,
        monogramFormRef: action.payload
      };
    }

    case 'SET_ONPAGE_CTA_REF': {
      return {
        ...state,
        onPageCTARef: action.payload
      };
    }

    case 'SET_ADDED_OPTIONAL_MONOGRAM': {
      return {
        ...state,
        addedOptionalMonogram: action.payload
      };
    }

    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
};

const initialState = {
  warning: '',
  quantityChange: '1',
  buttonText: 'Add to Bag',
  stickyAddedToCart: false,
  onPageAddedToCart: false,
  requireMonogram: false,
  wishlistRef: null,
  monogramFormRef: null,
  onPageCTARef: null,
  addedOptionalMonogram: false
};

const ProductProvider = ({ children }) => {
  const [state, dispatch] = useReducer(productReducer, initialState);

  const updateWarningMessage = (message) => dispatch({
    type: 'UPDATE_WARNING_MESSAGE',
    payload: message
  });

  const updateQuantity = (quantity) => {
    if (Number(quantity) > 1) {
      dispatch({
        type: 'UPDATE_QUANTITY',
        payload: quantity
      });
    }
  };

  const updateButtonText = (text) => dispatch({
    type: 'UPDATE_BUTTON_TEXT',
    payload: text
  });

  const onStickyAddedToCart = (boolean) => dispatch({
    type: 'SET_STICKY_ADDED_TO_CART',
    payload: boolean
  });

  const onOnPageAddedToCart = (boolean) => dispatch({
    type: 'SET_ONPAGE_ADDED_TO_CART',
    payload: boolean
  });

  const setRequireMonogram = (boolean) => dispatch({
    type: 'SET_REQUIRE_MONOGRAM',
    payload: boolean
  });

  const setWishListRef = (ref) => dispatch({
    type: 'SET_WISHLIST_REF',
    payload: ref
  });

  const setMonogramFormRef = (ref) => dispatch({
    type: 'SET_MONOGRAM_FORM_REF',
    payload: ref
  });

  const setOnPageCTARef = (ref) => dispatch({
    type: 'SET_ONPAGE_CTA_REF',
    payload: ref
  });

  const setAddedOptionalMonogram = (boolean) => dispatch({
    type: 'SET_ADDED_OPTIONAL_MONOGRAM',
    payload: boolean
  });

  const value = {
    state,
    updateWarningMessage,
    updateQuantity,
    updateButtonText,
    onStickyAddedToCart,
    setRequireMonogram,
    setWishListRef,
    setMonogramFormRef,
    setOnPageCTARef,
    onOnPageAddedToCart,
    setAddedOptionalMonogram,
    dispatch
  };

  return <ProductContext.Provider value={value}>{children}</ProductContext.Provider>;
};

ProductProvider.propTypes = {
  children: PropTypes.node.isRequired
};

const useProduct = () => {
  const context = useContext(ProductContext);

  if (context === undefined) {
    throw new Error('useProduct must be used within a ProductProvider');
  }

  return context;
};

export { ProductProvider, useProduct };
